/* tslint:disable:max-line-length */
import React, { useState } from 'react';
import classNames from 'classnames';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import { useSelector } from 'react-redux';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { Button, Heading, Spinner, Tooltip } from '@mm/components';
import { errorToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import {
  ArrowLeftOutlined,
  CloseOutlined,
  SyncOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import css from './AutomationEmailGenerateSlideout.module.css';
import TextInput from '../../../../../common/components/TextInput';
import Textarea from '../../../../../common/components/Textarea';
import { useAutomationsCRUDActions } from '@mm/state/actions/automationActions';
import { notification } from 'antd';

export interface IAutomationEmailGenerateSlideout {
  menuType: SlideOutMenuType;
  campaignId?: number;
  className?: string;
  onSubmit?: (generatedEmail: string) => void;
  getCurrentEmailString?: () => string;
}

export const AutomationEmailGenerateSlideout = ({
  menuType,
  campaignId,
  className,
  onSubmit = () => {},
  getCurrentEmailString = () => '',
}: IAutomationEmailGenerateSlideout) => {
  const [businessPageAddress, setBusinessPageAddress] = useState('');
  const [businessInfo, setBusinessInfo] = useState('');
  const [generatedEmail, setGeneratedEmail] = useState('');

  const [showGeneratedEmail, setShowGeneratedEmail] = useState(false);

  const [busy, setBusy] = useState(false);

  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const automationsCRUDActions = useAutomationsCRUDActions();

  const onGenerate = async () => {
    setBusy(true);
    try {
      const res = await automationsCRUDActions.getEmailCompletion(
        facebookPageId,
        {
          urlFromForm: businessPageAddress,
          companyInfoFromForm: businessInfo,
        },
      );

      setShowGeneratedEmail(true);

      if (res.completions?.length) {
        setGeneratedEmail(res.completions[0]);
      }
    } catch (err) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const onAttemptToCloseWhenBusy = () => {
    if (!busy) {
      closeSlideout();
    } else {
      notification.error({
        message: 'Cannot close while generating content',
        placement: 'topLeft',
      });
    }
  };

  const { setRef, close: closeSlideout } = useSlideoutOnClose(async () => {},
  menuType);

  const onCopyPaste = () => {
    closeSlideout();
    onSubmit(generatedEmail);
  };

  const onRewriteMyEmailClick = async () => {
    setBusy(true);

    setBusinessInfo('');
    setBusinessPageAddress('');

    try {
      const currentString = getCurrentEmailString();

      const res = await automationsCRUDActions.getEmailCompletion(
        facebookPageId,
        {
          urlFromForm: businessPageAddress,
          companyInfoFromForm: businessInfo,
          rewriteEmailBody: currentString,
        },
      );

      setShowGeneratedEmail(true);

      if (res.completions?.length) {
        setGeneratedEmail(res.completions[0]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <span className={css.back} onClick={onAttemptToCloseWhenBusy}>
          <ArrowLeftOutlined className={css.icon} />
        </span>
        <Heading size={4}>Auto-Email generation system</Heading>
        <Button
          color="secondary"
          css={{ container: css.rewrite }}
          onClick={onRewriteMyEmailClick}
          disabled={busy}
        >
          Re-write your current email
        </Button>
        <span className={css.close} onClick={onAttemptToCloseWhenBusy}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <span className={css.subtitle}>Your business home page </span>
        <p className={css.paragraph}>
          We will generate your email based on the content of this URL
        </p>

        <div className={css.inputContainer}>
          <TextInput
            className={css.input}
            placeholder="https://customers.ai/"
            value={businessPageAddress}
            onChange={e => setBusinessPageAddress(e.target.value)}
          />
        </div>

        <span className={classNames(css.subtitle, css.subtitleRow)}>
          Additional information about your business:{' '}
          <span className={css.footnote}>(optional)</span>
          <Tooltip
            useHover
            withoutLine
            css={{ container: css.tooltip }}
            anchorElement={<InfoCircleOutlined className={css.info} />}
            position="bottom"
          >
            Add additional information about your business that would not be
            included on your home page including, current sales/offering or what
            makes your product special.
          </Tooltip>
        </span>

        <div className={css.inputContainer}>
          <Textarea
            className={css.input}
            placeholder="My business is Mitchells plumbing, I specialize in emergency plumbing response in the Austin, Texas area. We are running a special discount to audit your plumbing for $99 that is available for the next 3 months. We are a fully insured company. "
            value={businessInfo}
            onChange={e => setBusinessInfo(e.target.value)}
          />
        </div>

        {showGeneratedEmail && (
          <>
            <span className={css.subtitle}>Generated Email</span>
            <div className={css.inputContainer}>
              <Textarea
                disabled={busy}
                className={css.input}
                value={generatedEmail}
                onChange={e => setGeneratedEmail(e.target.value)}
              />
            </div>
          </>
        )}

        <div className={css.actions}>
          {generatedEmail ? (
            <>
              <Button
                css={{ container: css.copy }}
                onClick={onCopyPaste}
                disabled={busy}
              >
                Copy & Paste to Email Body
              </Button>
              <Button
                color="secondary-blue"
                css={{ container: css.generate }}
                onClick={onGenerate}
                disabled={busy}
              >
                Try generating again
              </Button>
            </>
          ) : (
            <Button
              css={{ container: css.generate }}
              onClick={onGenerate}
              disabled={busy}
              leftIcon={busy ? <SyncOutlined /> : undefined}
            >
              {busy ? 'Generating email' : 'Generate Email'}
            </Button>
          )}

          {busy && <Spinner standalone size={18} show width={18} />}
        </div>
      </Menu.Content>
    </Menu>
  );
};
